import React, { FC } from "react";
import { Image, Modal } from "antd";
import ReactPlayer from "react-player";
import BeamIcon, { fileIconSrc } from "../MessageTypes/BeamIcon";
import { MediaMarkerData, getMediaKind } from "./MediaMapMarker";
import "./MediaViewer.scss";

/**
 * Full-size viewer for a media marker.
 *
 * The map card is a 232px teaser — this is what "click to open" actually lands
 * on. Routing matches what the live map already does in MapPanel/Map.tsx:
 * images go through antd's Image preview (zoom / rotate come for free), video
 * and audio go through ReactPlayer, and anything else is handed to the browser.
 */

interface MediaViewerProps {
  /** The marker to show. Null closes the viewer. */
  marker: MediaMarkerData | null;
  onClose: () => void;
}

const MediaViewer: FC<MediaViewerProps> = ({ marker, onClose }) => {
  if (!marker) return null;

  const kind = getMediaKind(marker.data?.mimeType);
  const url = marker.urls?.original?.url;
  const title = marker.data?.name || "Media";

  if (!url) return null;

  if (kind === "image") {
    // Controlled mode: the <Image> itself stays hidden so the map does not get
    // a second thumbnail, only its overlay opens.
    return (
      <Image
        style={{ display: "none" }}
        src={url}
        preview={{
          visible: true,
          src: url,
          onVisibleChange: (visible: boolean) => {
            if (!visible) onClose();
          },
        }}
      />
    );
  }

  return (
    <Modal
      title={title}
      visible
      onCancel={onClose}
      width={kind === "audio" ? 480 : 760}
      className="beam-modal media-viewer"
      footer={null}
      destroyOnClose
    >
      {kind === "video" || kind === "audio" ? (
        <ReactPlayer
          url={url}
          controls
          width="100%"
          height={kind === "audio" ? "54px" : "420px"}
        />
      ) : (
        // No inline renderer for documents — show what it is and let the
        // browser take it from here.
        <div className="media-viewer__file">
          <img src={fileIconSrc(title)} alt="" />
          <p>{title}</p>
          <a
            className="media-viewer__open"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <BeamIcon name="expand" size={14} /> Open file
          </a>
        </div>
      )}

      <dl className="media-viewer__meta">
        {marker.author && (
          <>
            <dt>Author</dt>
            <dd>{marker.author}</dd>
          </>
        )}
        {marker.address && (
          <>
            <dt>Location</dt>
            <dd>{marker.address}</dd>
          </>
        )}
      </dl>
    </Modal>
  );
};

export default MediaViewer;
